import { createReducer } from 'redux-act'

import FiltersState, { InitializeFiltersPayload, FilterClickPayload } from './types'
import {
    initializeFilters,
    setFiltersConfiguration,
    commitFiltersConfiguration,
    revertFiltersConfiguration,
    setFiltersError,
    setLoading,
    setBlocking,
    setLastDataPathEncoded,
    setSearchRequestId,
    clearSearchRequestId,
    pushModal,
    popModal,
} from './actions'
import { FilterModalType } from '../../components/app-components/filter-sort-section/types'
import initialState from './state'

const _initializeFilters = (_state: FiltersState, payload: InitializeFiltersPayload): FiltersState => ({
    ..._state,
    filtersConfigurationResponse: payload.config,
    committedFiltersConfigurationResponse: payload.config,
    regionNodeId: payload.regionNodeId,
    spaMode: payload.spaMode,
    // NOTE: Do NOT set lastDataPathEncoded here.
    // lastDataPathEncoded is only meant to track URL changes from filter interactions
    // (filterClick, filterClear, filterSubmit). Setting it during initialization
    // would trigger the URL-rewrite useEffect in search-result-listing-content
    // and change the browser URL on the very first page load.
    error: null,
    searchRequestId: undefined, // Reset on new search so BFF generates a fresh ID
})

const _setFiltersConfiguration = (state: FiltersState, payload: FilterClickPayload): FiltersState => ({
    ...state,
    filtersConfigurationResponse: payload.config,
    committedFiltersConfigurationResponse: payload.shouldCommit
        ? payload.config
        : state.committedFiltersConfigurationResponse,
    searchRequestId: payload.shouldCommit ? undefined : state.searchRequestId,
})

const _commitFiltersConfiguration = (state: FiltersState): FiltersState => ({
    ...state,
    committedFiltersConfigurationResponse: state.filtersConfigurationResponse,
    searchRequestId: undefined,
})

const _revertFiltersConfiguration = (state: FiltersState): FiltersState => ({
    ...state,
    filtersConfigurationResponse: state.committedFiltersConfigurationResponse,
})

const _setFiltersError = (state: FiltersState, payload: Error | null): FiltersState => ({
    ...state,
    error: payload,
})

const _setLoading = (state: FiltersState, payload: boolean): FiltersState => ({
    ...state,
    isLoading: payload,
})

const _setBlocking = (state: FiltersState, payload: boolean): FiltersState => ({
    ...state,
    isBlocking: payload,
})

const _setLastDataPathEncoded = (state: FiltersState, payload: string): FiltersState => ({
    ...state,
    lastDataPathEncoded: payload,
})

const _setSearchRequestId = (state: FiltersState, payload: string): FiltersState => ({
    ...state,
    searchRequestId: payload,
})

const _clearSearchRequestId = (state: FiltersState): FiltersState => ({
    ...state,
    searchRequestId: undefined,
})

const _pushModal = (state: FiltersState, payload: FilterModalType): FiltersState => ({
    ...state,
    modalStack: [...state.modalStack, payload],
})

const _popModal = (state: FiltersState): FiltersState => {
    if (state.modalStack.length === 0) {
        return state
    }
    return {
        ...state,
        modalStack: state.modalStack.slice(0, -1),
    }
}

const reducer = createReducer<FiltersState>(on => {
    on(initializeFilters, _initializeFilters)
    on(setFiltersConfiguration, _setFiltersConfiguration)
    on(commitFiltersConfiguration, _commitFiltersConfiguration)
    on(revertFiltersConfiguration, _revertFiltersConfiguration)
    on(setFiltersError, _setFiltersError)
    on(setLoading, _setLoading)
    on(setBlocking, _setBlocking)
    on(setLastDataPathEncoded, _setLastDataPathEncoded)
    on(setSearchRequestId, _setSearchRequestId)
    on(clearSearchRequestId, _clearSearchRequestId)
    on(pushModal, _pushModal)
    on(popModal, _popModal)
}, initialState)

export default reducer
